<template>
  <div class="approval-process-modal">
    <!-- 问号按钮 -->
    <el-button
      v-if="showButton && busId"
      type="text"
      icon="el-icon-question"
      class="approval-question-btn"
      :title="$t('查看审批流程')"
      :disabled="disabled"
      @click="handleButtonClick"
    />

    <div v-if="dialogVisible" class="dialog-wrapper">
      <el-dialog
        :title="approvalData ? approvalData.title : ''"
        :visible="dialogInnerVisible"
        :before-close="handleClose"
        width="500px"
        append-to-body
        class="approval-process-dialog"
        @close="handleClose"
      >
        <div v-loading="loading" class="approval-content">
          <!-- 没有数据时的兜底显示 -->
          <div v-if="!loading && !approvalData" class="no-data-container">
            <div class="no-data-content">
              <p>{{ $t("暂无审批数据") }}</p>
            </div>
          </div>

          <!-- 审批信息头部 -->
          <div v-else-if="approvalData" class="approval-header">
            <div class="approval-info">
              <div class="info-item">
                <label>{{ $t("审批编号") }}：</label>
                <span>{{ approvalData.businessId }}</span>
              </div>
              <div class="info-item">
                <label>{{ $t("结果") }}：</label>
                <span
                  :class="
                    getResultClass(approvalData.status, approvalData.result)
                  "
                >
                  {{ getResultText(approvalData.status, approvalData.result) }}
                </span>
              </div>
            </div>
          </div>

          <!-- 审批流程 -->
          <div v-if="approvalData && approvalData.nodeList && approvalData.nodeList.length > 0" class="approval-flow">
            <h3>{{ $t("审批流程") }}</h3>
            <div class="flow-timeline">
              <div
                v-for="(node, nodeIndex) in approvalData.nodeList"
                :key="nodeIndex"
                class="timeline-node"
              >
                <!-- 时间线圆圈和连线 -->
                <div class="timeline-indicator">
                  <div class="timeline-circle"></div>
                  <div v-if="nodeIndex < approvalData.nodeList.length - 1" class="timeline-line"></div>
                </div>

                <!-- 节点内容 -->
                <div class="timeline-content">
                  <div class="node-header">
                    <div class="node-title">
                      {{ node.nodeName }}
                    </div>
                  </div>
                  <div class="node-users">
                    <div
                      v-for="(user, userIndex) in node.userList"
                      :key="userIndex"
                      class="user-item"
                    >
                      <div
                        class="user-info"
                        :class="getUserBorderClass(user.taskStatus, user.taskResult)"
                      >
                        <span class="user-name">{{ user.userName }}</span>
                        <span
                          class="user-result"
                          :class="getUserResultTextClass(user.taskStatus, user.taskResult)"
                        >{{
                          getUserResultText(user.taskStatus, user.taskResult)
                        }}</span>
                        <span v-if="user.finishTime" class="finish-time">{{
                          user.finishTime
                        }}</span>
                      </div>
                      <!-- 显示备注信息 -->
                      <div v-if="user.remark" class="user-remark">
                        <span class="remark-label">{{ $t('评论') }}：</span>
                        <span class="remark-content">{{ user.remark }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 审批流程为空时的兜底显示 -->
          <div v-else-if="!loading && approvalData && (!approvalData.nodeList || approvalData.nodeList.length === 0)" class="no-data-container">
            <div class="no-data-content">
              <i class="el-icon-info"></i>
              <p>{{ $t("暂无审批流程") }}</p>
            </div>
          </div>
        </div>

        <template #footer>
          <div class="dialog-footer">
            <el-button :disabled="disabled" @click="handleClose">
              {{ $t("关闭") }}
            </el-button>
          </div>
        </template>
      </el-dialog>
    </div>
  </div>
</template>

<script>
import { post } from '@/http/request'

export default {
  name: 'ApprovalProcessModal',
  props: {
    busId: {
      type: String,
      default: '', // 业务ID
    },
    dingCode: {
      type: String,
      default: '', // 审批流程编码
    },
    showButton: {
      type: Boolean,
      default: true, // 是否显示问号按钮
    },
    disabled: {
      type: Boolean,
      default: false, // 是否禁用
    },
  },
  data() {
    return {
      loading: false,
      approvalData: null,
      visible: false,
      dialogInnerVisible: false,
    }
  },
  computed: {
    dialogVisible() {
      return this.visible
    },
  },
  watch: {
    visible(newVal) {
      if (newVal && this.busId) {
        this.getApprovalProcess()
        // 延迟显示内部对话框以保留动画效果
        this.$nextTick(() => {
          setTimeout(() => {
            this.dialogInnerVisible = true
          }, 10)
        })
      } else {
        this.approvalData = null
        // 立即隐藏内部对话框
        this.dialogInnerVisible = false
      }
    },
    busId(newVal) {
      if (newVal && this.visible) {
        this.getApprovalProcess()
      }
    },
  },
  methods: {
    // 获取审批流程详情
    async getApprovalProcess() {
      if (!this.busId) return

      this.loading = true
      try {
        const res = await post('ding/callback/getBusInstance', {
          busId: this.busId,
          dingCode: this.dingCode,
        })

        if (res.code === 0) {
          this.approvalData = res.data
        } else {
          this.$message.error(res.message || this.$t('获取审批流程失败'))
        }
      } catch (error) {
        this.$message.error(this.$t('获取审批流程失败'))
        console.error('获取审批流程失败:', error)
      } finally {
        this.loading = false
      }
    },

    // 获取结果文本
    getResultText(status, result) {
      if (status === 'COMPLETED') {
        return (
          {
            agree: this.$t('已通过'),
            refuse: this.$t('已拒绝'),
          }[result] || result
        )
      }
      return (
        {
          RUNNING: this.$t('审批中'),
          TERMINATED: this.$t('撤销'),
          CANCELED: this.$t('撤销'),
          COMPLETED: this.$t('已完成'),
        }[status] || status
      )
    },

    // 获取结果样式类
    getResultClass(status, result) {
      if (status === 'COMPLETED') {
        return result === 'agree' ? 'result-success' : 'result-danger'
      }
      return (
        {
          RUNNING: 'result-warning',
          TERMINATED: 'result-info',
          CANCELED: 'result-info',
        }[status] || ''
      )
    },

    // 获取任务状态文本
    getTaskStatusText(status) {
      return (
        {
          RUNNING: this.$t('处理中'),
          PAUSED: this.$t('暂停'),
          CANCELED: this.$t('取消'),
          COMPLETED: this.$t('完成'),
          TERMINATED: this.$t('已撤销'),
        }[status] || status
      )
    },

    // 获取任务结果文本
    getTaskResultText(result) {
      return (
        {
          AGREE: this.$t('同意'),
          REFUSE: this.$t('拒绝'),
          REDIRECTED: this.$t('转交'),
        }[result] || result
      )
    },

    // 获取用户结果显示文本（先看taskStatus，再看taskResult）
    getUserResultText(taskStatus, taskResult) {
      if (taskStatus === 'COMPLETED') {
        const statusText = ''
        if (taskResult && taskResult !== 'NONE') {
          const resultText = this.getTaskResultText(taskResult)
          return resultText
        }
        return statusText
      }
      if (taskResult && taskResult !== 'NONE') {
        return this.getTaskResultText(taskResult)
      }
      return this.getTaskStatusText(taskStatus)
    },

    // 获取用户状态样式类
    getUserStatusClass(status) {
      return (
        {
          RUNNING: 'user-running',
          COMPLETED: 'user-completed',
          CANCELED: 'user-canceled',
          TERMINATED: 'user-terminated',
        }[status] || ''
      )
    },

    // 获取用户边框样式类
    getUserBorderClass(taskStatus, taskResult) {
      if (taskStatus === 'COMPLETED') {
        if (taskResult === 'AGREE') {
          return 'border-success'
        } else if (taskResult === 'REFUSE') {
          return 'border-danger'
        }
      } else if (taskStatus === 'RUNNING') {
        return 'border-warning'
      } else if (taskStatus === 'TERMINATED' || taskStatus === 'CANCELED') {
        return 'border-info'
      }
      return 'border-default'
    },

    // 获取用户结果文字样式类
    getUserResultTextClass(taskStatus, taskResult) {
      if (taskStatus === 'COMPLETED') {
        if (taskResult === 'AGREE') {
          return 'text-success'
        } else if (taskResult === 'REFUSE') {
          return 'text-danger'
        }
      } else if (taskStatus === 'RUNNING') {
        return 'text-warning'
      } else if (taskStatus === 'TERMINATED' || taskStatus === 'CANCELED') {
        return 'text-info'
      }
      return 'text-default'
    },

    // 获取状态图标
    getStatusIcon(status, result) {
      if (status === 'COMPLETED') {
        return result === 'AGREE'
          ? 'el-icon-check'
          : result === 'REFUSE'
            ? 'el-icon-close'
            : 'el-icon-check'
      }
      return (
        {
          RUNNING: 'el-icon-loading',
          PAUSED: 'el-icon-video-pause',
          CANCELED: 'el-icon-close',
          TERMINATED: 'el-icon-close',
        }[status] || 'el-icon-question'
      )
    },

    // 显示审批流程弹窗
    show() {
      this.visible = true
    },

    // 处理按钮点击
    handleButtonClick() {
      this.visible = true
    },

    // 关闭弹窗
    handleClose() {
      this.dialogInnerVisible = false
      // 延迟隐藏外部包装器以保留退出动画
      setTimeout(() => {
        this.visible = false
        this.loading = false
        this.approvalData = null
      }, 200)
    },
  },
}
</script>

<style lang="scss" scoped>
.approval-process-modal {
  display: inline-block;
}

.dialog-wrapper {
  display: contents;
}
.approval-question-btn {
  display: inline-block;
  padding: 0 8px !important;
  color: #409eff !important;
  height: 14px;

  &:hover {
    color: #66b1ff !important;
    background-color: #ecf5ff !important;
  }

  ::v-deep .el-icon-question {
    font-size: 14px;
  }
}
.approval-process-dialog {
  .approval-content {
    min-height: 200px;
  }

  .no-data-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
    text-align: center;

    .no-data-content {
      color: #909399;

      i {
        font-size: 48px;
        margin-bottom: 16px;
        display: block;
      }

      p {
        font-size: 14px;
        margin: 0;
        line-height: 1.5;
      }
    }
  }

  .approval-header {
    margin-bottom: 24px;
    padding: 16px;
    background-color: #f8f9fa;
    border-radius: 6px;

    .approval-info {
      .info-item {
        display: flex;
        align-items: center;
        margin-bottom: 8px;

        &:last-child {
          margin-bottom: 0;
        }

        label {
          font-weight: 600;
          color: #333;
          min-width: 80px;
        }

        span {
          color: #666;

          &.result-success {
            color: #67c23a;
            font-weight: 600;
          }

          &.result-danger {
            color: #f56c6c;
            font-weight: 600;
          }

          &.result-warning {
            color: #e6a23c;
            font-weight: 600;
          }

          &.result-info {
            color: #909399;
            font-weight: 600;
          }
        }
      }
    }
  }

  .approval-flow {
    h3 {
      margin-bottom: 16px;
      color: #333;
      font-size: 16px;
      font-weight: 600;
    }

    .flow-timeline {
      .timeline-node {
        display: flex;
        margin-bottom: 32px;
        position: relative;

        &:last-child {
          margin-bottom: 0;
        }

        .timeline-indicator {
          display: flex;
          flex-direction: column;
          align-items: center;
          margin-right: 16px;
          min-width: 24px;
          position: relative;

          .timeline-circle {
            width: 12px;
            height: 12px;
            border-radius: 50%;
            background-color: #409eff;
            border: 2px solid #ffffff;
            box-shadow: 0 0 0 2px #e4e7ed;
            flex-shrink: 0;
            z-index: 2;
            position: relative;
          }

          .timeline-line {
            width: 2px;
            height: calc(100% + 24px);
            background-color: #e4e7ed;
            position: absolute;
            top: 16px;
            left: 50%;
            transform: translateX(-50%);
            z-index: 1;
          }
        }

        .timeline-content {
          flex: 1;
          min-width: 0; /* 防止内容溢出 */

          .node-header {
            margin-bottom: 12px;

            .node-title {
              font-weight: 600;
              color: #333;
              font-size: 14px;
            }
          }

          .node-users {
            .user-item {
              margin-bottom: 8px;

              &:last-child {
                margin-bottom: 0;
              }

              .user-info {
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: 8px 12px;
                background-color: #f8f9fa;
                border-radius: 6px;
                border-left: 3px solid #409eff;

                // 根据状态显示不同的左边框颜色
                &.border-success {
                  border-left-color: #67c23a; /* 绿色 - 同意 */
                }

                &.border-danger {
                  border-left-color: #f56c6c; /* 红色 - 拒绝 */
                }

                &.border-warning {
                  border-left-color: #e6a23c; /* 黄色 - 进行中 */
                }

                &.border-default {
                  border-left-color: #409eff; /* 蓝色 - 默认 */
                }

                &.border-info {
                  border-left-color: #909399; /* 灰色 - 撤销 */
                }

                .user-name {
                  font-weight: 600;
                  color: #333;
                  flex: 0 0 auto;
                  margin-right: 16px;
                }

                .user-result {
                  color: #666;
                  font-size: 14px;
                  flex: 1;

                  // 根据状态显示不同的文字颜色
                  &.text-success {
                    color: #67c23a; /* 绿色 - 同意 */
                    font-weight: 600;
                  }

                  &.text-danger {
                    color: #f56c6c; /* 红色 - 拒绝 */
                    font-weight: 600;
                  }

                  &.text-warning {
                    color: #e6a23c; /* 黄色 - 进行中 */
                    font-weight: 600;
                  }

                  &.text-default {
                    color: #666; /* 默认颜色 */
                  }

                  &.text-info {
                    color: #909399; /* 灰色 - 撤销 */
                    font-weight: 600;
                  }
                }

                .finish-time {
                  color: #909399;
                  font-size: 12px;
                  flex: 0 0 auto;
                }
              }

              .user-remark {
                margin-top: 8px;
                padding: 8px 12px;
                background-color: #f5f5f5;
                border-radius: 4px;

                .remark-label {
                  font-weight: 500;
                  color: #666666;
                  font-size: 12px;
                }

                .remark-content {
                  color: #666666;
                  font-size: 13px;
                  line-height: 1.4;
                }
              }
            }
          }
        }
      }
    }
  }

  .approval-times {
    padding: 16px;
    background-color: #f8f9fa;
    border-radius: 6px;

    .time-item {
      display: flex;
      align-items: center;
      margin-bottom: 8px;

      &:last-child {
        margin-bottom: 0;
      }

      label {
        font-weight: 600;
        color: #333;
        min-width: 80px;
      }

      span {
        color: #666;
      }
    }
  }
}
</style>

